import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { ShowCard } from './ShowCard';
import { formatTime, getSlotSpan } from '../../utils/helpers';
import { TIME_SLOTS } from '../../utils/constants';

const TOTAL_SLOTS = 48;

export function TimeSlot({
  dayId,
  time,
  entries,
  showsMap,
  canEdit,
  onEdit,
  onDelete,
  onAddEntry,
  activeId,
  isTimeColumn = false,
}) {
  const droppableId = `${dayId}-${time}`;
  const { isOver, setNodeRef } = useDroppable({
    id: droppableId,
    data: { dayId, time },
  });

  if (isTimeColumn) {
    return (
      <div className="grid-time-cell">
        {formatTime(time)}
      </div>
    );
  }

  const slotIndex = TIME_SLOTS.indexOf(time);

  let entry = null;
  let isOverflow = false;
  let visibleSpan = 0;

  // Same-day match: clip span at midnight so the card stops at 23:30
  entry = entries?.find((e) => {
    if (e.day !== dayId) return false;
    const entryStart = TIME_SLOTS.indexOf(e.time);
    if (entryStart < 0) return false;
    const span = getSlotSpan(e.duration ?? 30);
    const endSlot = Math.min(entryStart + span, TOTAL_SLOTS);
    return slotIndex >= entryStart && slotIndex < endSlot;
  });

  if (entry) {
    const entryStart = TIME_SLOTS.indexOf(entry.time);
    const fullSpan = getSlotSpan(entry.duration ?? 30);
    visibleSpan = Math.min(fullSpan, TOTAL_SLOTS - entryStart);
  }

  // Cross-midnight: check if an entry from the previous day overflows into this day
  if (!entry && dayId > 1) {
    const prevDay = dayId - 1;
    entry = entries?.find((e) => {
      if (e.day !== prevDay) return false;
      const entryStart = TIME_SLOTS.indexOf(e.time);
      if (entryStart < 0) return false;
      const span = getSlotSpan(e.duration ?? 30);
      const endSlot = entryStart + span;
      if (endSlot <= TOTAL_SLOTS) return false;
      const overflowSlots = endSlot - TOTAL_SLOTS;
      return slotIndex < overflowSlots;
    });

    if (entry) {
      isOverflow = true;
      const entryStart = TIME_SLOTS.indexOf(entry.time);
      const fullSpan = getSlotSpan(entry.duration ?? 30);
      visibleSpan = (entryStart + fullSpan) - TOTAL_SLOTS;
    }
  }

  const isStartSlot = entry && !isOverflow && TIME_SLOTS.indexOf(entry.time) === slotIndex;
  const isOverflowStartSlot = entry && isOverflow && slotIndex === 0;
  const show = entry ? showsMap?.[entry.show_id] : null;

  const handleSlotClick = () => {
    if (canEdit && !entry && onAddEntry) {
      onAddEntry(dayId, time);
    }
  };

  return (
    <div
      ref={setNodeRef}
      className={`grid-slot-cell ${isOver ? 'drag-over' : ''} ${entry ? 'occupied' : ''} ${canEdit && !entry ? 'empty-editable' : ''}`}
      onClick={handleSlotClick}
    >
      {(isStartSlot || isOverflowStartSlot) && entry && show && (
        <ShowCard
          entry={entry}
          show={show}
          canEdit={canEdit && !isOverflow}
          onEdit={!isOverflow ? onEdit : undefined}
          onDelete={!isOverflow ? onDelete : undefined}
          isDragging={!isOverflow && String(entry.id) === activeId}
          visibleSpan={visibleSpan}
          isOverflow={isOverflow}
        />
      )}
      {canEdit && !entry && (
        <span className="slot-add-hint">+</span>
      )}
    </div>
  );
}
